import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  Users, 
  UserPlus, 
  UserMinus,
  Check, 
  X, 
  Loader2, 
  Clock,
  UserCheck,
  Search,
  Bell,
  MessageCircle,
  ArrowLeft
} from "lucide-react";
import { Input } from "@/components/ui/input";

const ConnectionsPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("requests");
  const [pendingRequests, setPendingRequests] = useState([]);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [requestsRes, connectionsRes] = await Promise.all([
        axios.get(`${API}/social/connections/requests`),
        axios.get(`${API}/social/connections`)
      ]);
      
      setPendingRequests(requestsRes.data?.requests || requestsRes.data || []);
      setConnections(connectionsRes.data?.connections || connectionsRes.data || []);
    } catch (error) {
      console.error("Veri yüklenemedi:", error);
      toast.error("Veriler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const handleRespond = async (connectionId, accept) => {
    setProcessingId(connectionId);
    try {
      await axios.post(`${API}/social/connections/${connectionId}/respond`, {
        accept
      });
      
      if (accept) {
        toast.success("Bağlantı isteği kabul edildi!");
        // Move from pending to connections
        const acceptedRequest = pendingRequests.find(r => r.id === connectionId);
        if (acceptedRequest) {
          setConnections(prev => [...prev, {
            ...acceptedRequest,
            status: 'accepted'
          }]);
        }
      } else {
        toast.success("Bağlantı isteği reddedildi");
      }
      
      setPendingRequests(prev => prev.filter(r => r.id !== connectionId));
    } catch (error) {
      toast.error(error.response?.data?.detail || "İşlem başarısız");
    } finally {
      setProcessingId(null);
    }
  };

  const handleRemoveConnection = async (userId) => {
    setProcessingId(userId);
    try {
      await axios.delete(`${API}/social/connections/${userId}`);
      setConnections(prev => prev.filter(c => c.userId !== userId && c.connectedUserId !== userId));
      toast.success("Bağlantı kaldırıldı");
    } catch (error) {
      toast.error("Bağlantı kaldırılamadı");
    } finally {
      setProcessingId(null);
    }
  };

  const getPositionLabel = (position) => {
    const labels = {
      forward: "Forvet",
      midfielder: "Orta Saha",
      defender: "Defans",
      goalkeeper: "Kaleci"
    };
    return labels[position] || position;
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = (now - date) / 1000;
    
    if (diff < 60) return "Az önce";
    if (diff < 3600) return `${Math.floor(diff / 60)} dk önce`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} saat önce`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} gün önce`;
    return date.toLocaleDateString('tr-TR');
  };

  const filteredConnections = connections.filter(conn => 
    conn.userName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    conn.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-bg transition-colors duration-300">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg transition-colors duration-300">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="container mx-auto px-4 py-6 max-w-2xl">
        {/* Geri Butonu */}
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4 text-gray-600 hover:text-gray-800 hover:bg-gray-100 -ml-2"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Geri
        </Button>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Users className="h-6 w-6 text-emerald-600" />
            Bağlantılar
          </h1>
          <Badge variant="outline" className="text-emerald-600">
            {connections.length} Bağlantı
          </Badge>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 mb-6">
            <TabsTrigger value="requests" className="gap-2">
              <Bell className="h-4 w-4" />
              Bekleyen İstekler
              {pendingRequests.length > 0 && (
                <Badge className="ml-1 bg-red-500 text-white text-xs px-1.5 py-0.5">
                  {pendingRequests.length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="connections" className="gap-2">
              <UserCheck className="h-4 w-4" />
              Bağlantılarım
            </TabsTrigger>
          </TabsList>

          {/* Pending Requests Tab */}
          <TabsContent value="requests">
            {pendingRequests.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Clock className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-500 mb-2">Bekleyen istek yok</p>
                  <p className="text-sm text-gray-400">
                    Size gelen bağlantı istekleri burada görünecek
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {pendingRequests.map((request) => (
                  <Card key={request.id} className="overflow-hidden">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <Avatar 
                          className="h-12 w-12 cursor-pointer"
                          onClick={() => navigate(`/profile/${request.senderId || request.userId}`)}
                        >
                          <AvatarImage src={request.senderImage || request.userImage} />
                          <AvatarFallback className="bg-emerald-600 text-white">
                            {(request.senderName || request.userName)?.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p 
                              className="font-semibold hover:underline cursor-pointer"
                              onClick={() => navigate(`/profile/${request.senderId || request.userId}`)}
                            >
                              {request.senderName || request.userName}
                            </p>
                            {request.senderPosition && (
                              <Badge variant="outline" className="text-xs">
                                {getPositionLabel(request.senderPosition)}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {formatTime(request.createdAt)}
                          </p>
                        </div>

                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleRespond(request.id, true)}
                            disabled={processingId === request.id}
                            className="bg-emerald-600 hover:bg-emerald-700"
                          >
                            {processingId === request.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Check className="h-4 w-4" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleRespond(request.id, false)}
                            disabled={processingId === request.id}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Connections Tab */}
          <TabsContent value="connections">
            {/* Search */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Bağlantılarda ara..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>

            {filteredConnections.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <Users className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <p className="text-gray-500 mb-2">
                    {searchQuery ? "Sonuç bulunamadı" : "Henüz bağlantınız yok"}
                  </p>
                  <p className="text-sm text-gray-400 mb-4">
                    {searchQuery 
                      ? "Farklı bir arama deneyin" 
                      : "Diğer oyuncuların profillerinden bağlantı isteği gönderin"}
                  </p>
                  {!searchQuery && (
                    <Button onClick={() => navigate('/social')} className="bg-emerald-600 hover:bg-emerald-700">
                      <Search className="h-4 w-4 mr-2" />
                      Oyuncu Keşfet
                    </Button>
                  )}
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {filteredConnections.map((connection) => {
                  const connUserId = connection.userId === user?.id 
                    ? connection.connectedUserId 
                    : connection.userId;
                  const connUserName = connection.userName || connection.name;
                  const connUserImage = connection.userImage || connection.profileImage;
                  
                  return (
                    <Card key={connection.id || connUserId} className="overflow-hidden">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <Avatar 
                            className="h-12 w-12 cursor-pointer"
                            onClick={() => navigate(`/profile/${connUserId}`)}
                          >
                            <AvatarImage src={connUserImage} />
                            <AvatarFallback className="bg-emerald-600 text-white">
                              {connUserName?.charAt(0)}
                            </AvatarFallback>
                          </Avatar>
                          
                          <div className="flex-1">
                            <p 
                              className="font-semibold hover:underline cursor-pointer"
                              onClick={() => navigate(`/profile/${connUserId}`)}
                            >
                              {connUserName}
                            </p>
                            {connection.position && (
                              <Badge variant="outline" className="text-xs mt-1">
                                {getPositionLabel(connection.position)}
                              </Badge>
                            )}
                          </div>

                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => navigate(`/messages/${connUserId}`)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <MessageCircle className="h-4 w-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleRemoveConnection(connUserId)}
                              disabled={processingId === connUserId}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              {processingId === connUserId ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <UserMinus className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ConnectionsPage;
